import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Iter 31.220+ — Smart-Fix Album Dates.
 *
 * For google_photo rows with date_source=unknown sitting in a Google
 * Photos album whose name carries a year ("Christmas 2019", "Italy
 * 2014-08"), bulk-set the timestamp to mid-year. Two-step: dry-run
 * first to preview the inference, then apply.
 */
function SmartFixAlbumDatesButton({ onApplied }) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [err, setErr] = useState("");

    const dryRun = async () => {
        setLoading(true);
        setErr("");
        try {
            const { data } = await api.post(
                "/life-entries/cleanup/smart-fix-album-dates",
                { dry_run: true },
            );
            setPreview(data);
        } catch (e) {
            setErr(e?.message || "preview failed");
        } finally {
            setLoading(false);
        }
    };

    const apply = async () => {
        setApplying(true);
        setErr("");
        try {
            await api.post(
                "/life-entries/cleanup/smart-fix-album-dates",
                { dry_run: false },
            );
            setPreview(null);
            onApplied && onApplied();
        } catch (e) {
            setErr(e?.message || "apply failed");
        } finally {
            setApplying(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={dryRun}
                disabled={loading}
                data-testid="cleanup-smart-fix-preview-btn"
                className="font-body text-xs uppercase tracking-[0.18em] px-3 py-1 border border-ink/40 hover:bg-ink/5"
                title="Infer dates from Google Photos album folder names"
            >
                {loading ? "Scanning…" : "Smart-fix album dates"}
            </button>
            {preview && (
                <div
                    className="w-full mt-2 p-3 border border-ink/30 bg-ink/5 font-body text-xs"
                    data-testid="cleanup-smart-fix-preview"
                >
                    <div className="font-heading uppercase tracking-[0.18em] text-ink mb-2">
                        Preview · {preview.rows_inferable} rows in {preview.albums_inferable} albums
                    </div>
                    {err && <div className="text-stamp-red mb-2">{err}</div>}
                    <ul className="space-y-1 max-h-64 overflow-y-auto mb-3">
                        {(preview.by_album || []).map((a) => (
                            <li key={a.album} className="flex justify-between border-b border-ink/10 py-1">
                                <span className="truncate mr-2">
                                    {a.album}
                                    <span className="text-ink-soft ml-2 text-[10px]">({a.match_kind})</span>
                                </span>
                                <span className="font-mono whitespace-nowrap">
                                    {a.inferred_year} · {a.row_count} rows
                                </span>
                            </li>
                        ))}
                    </ul>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={apply}
                            disabled={applying || preview.rows_inferable === 0}
                            data-testid="cleanup-smart-fix-apply-btn"
                            className="font-body text-xs uppercase tracking-[0.18em] px-3 py-1 bg-ink text-paper hover:opacity-80 disabled:opacity-30"
                        >
                            {applying ? "Applying…" : `Apply to ${preview.rows_inferable} rows`}
                        </button>
                        <button
                            type="button"
                            onClick={() => setPreview(null)}
                            className="font-body text-xs uppercase tracking-[0.18em] px-3 py-1 border border-ink/40 hover:bg-ink/5"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

export default function CleanupQueuePanel() {
    const [open, setOpen] = useState(false);
    const [summary, setSummary] = useState(null);
    const [items, setItems] = useState([]);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState("");
    const [tab, setTab] = useState("queue");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const [sumRes, lowRes, tagRes] = await Promise.all([
                api.get("/life-entries/cleanup/summary"),
                api.get("/life-entries/low-confidence?max_confidence=0.7&limit=25"),
                api.get("/life-entries/tags?min_count=3&limit=50"),
            ]);
            setSummary(sumRes.data);
            setItems(lowRes.data?.items || []);
            setTags(tagRes.data?.tags || []);
        } catch (e) {
            setError(e?.message || "load failed");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open && !summary) load();
    }, [open, summary, load]);

    const toggleSelect = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelected(next);
    };

    const bulkConfirm = async () => {
        if (!selected.size) return;
        setConfirming(true);
        try {
            await api.post("/life-entries/cleanup/bulk-confirm", {
                ids: Array.from(selected),
                note: "operator review from cleanup panel",
            });
            setSelected(new Set());
            await load();
        } catch (e) {
            setError(e?.message || "bulk confirm failed");
        } finally {
            setConfirming(false);
        }
    };

    // Total low-confidence count from summary.tiers (sum all <1.0 buckets).
    const lowCount = summary
        ? (summary.tiers || []).reduce((a, t) => a + (t.count || 0), 0)
        : null;

    return (
        <div
            className="paper-card border border-ink/30 mb-4"
            data-testid="cleanup-queue-panel"
        >
            <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-ink/5 text-left"
                onClick={() => setOpen(!open)}
                data-testid="cleanup-queue-toggle"
            >
                <div>
                    <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink">
                        Cleanup Queue
                    </h3>
                    <p className="font-body text-xs text-ink-soft mt-1">
                        Low-confidence rows + tag surface. {open ? "Hide" : "Open"} to review.
                    </p>
                </div>
                <div className="text-right">
                    {lowCount !== null && (
                        <div
                            className="font-heading text-2xl text-ink"
                            data-testid="cleanup-queue-count"
                        >
                            {lowCount}
                        </div>
                    )}
                    <div className="font-body text-[10px] uppercase tracking-[0.18em] text-ink-soft">
                        {open ? "Close" : "Open"}
                    </div>
                </div>
            </button>

            {open && (
                <div className="border-t border-ink/15 p-4">
                    {error && (
                        <div
                            className="font-body text-xs text-stamp-red mb-3"
                            data-testid="cleanup-queue-error"
                        >
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 mb-4 text-xs uppercase tracking-[0.18em] font-body">
                        <button
                            type="button"
                            onClick={() => setTab("queue")}
                            data-testid="cleanup-tab-queue"
                            className={`px-2 py-1 ${tab === "queue" ? "bg-ink text-paper" : "text-ink-soft hover:text-ink"}`}
                        >
                            Queue ({items.length})
                        </button>
                        <button
                            type="button"
                            onClick={() => setTab("tags")}
                            data-testid="cleanup-tab-tags"
                            className={`px-2 py-1 ${tab === "tags" ? "bg-ink text-paper" : "text-ink-soft hover:text-ink"}`}
                        >
                            Tags ({tags.length})
                        </button>
                        <button
                            type="button"
                            onClick={() => setTab("breakdown")}
                            data-testid="cleanup-tab-breakdown"
                            className={`px-2 py-1 ${tab === "breakdown" ? "bg-ink text-paper" : "text-ink-soft hover:text-ink"}`}
                        >
                            Breakdown
                        </button>
                    </div>

                    {loading && (
                        <div className="font-body text-xs text-ink-soft">Loading…</div>
                    )}

                    {!loading && tab === "queue" && (
                        <>
                            <div className="flex items-center gap-2 mb-3 flex-wrap">
                                <button
                                    type="button"
                                    onClick={bulkConfirm}
                                    disabled={!selected.size || confirming}
                                    data-testid="cleanup-bulk-confirm-btn"
                                    className="font-body text-xs uppercase tracking-[0.18em] px-3 py-1 bg-ink text-paper hover:opacity-80 disabled:opacity-30"
                                >
                                    {confirming ? "Confirming…" : `Confirm ${selected.size} selected`}
                                </button>
                                <button
                                    type="button"
                                    onClick={load}
                                    className="font-body text-xs uppercase tracking-[0.18em] px-3 py-1 border border-ink/40 hover:bg-ink/5"
                                >
                                    Refresh
                                </button>
                                <SmartFixAlbumDatesButton onApplied={load} />
                            </div>
                            <div className="space-y-1 max-h-96 overflow-y-auto">
                                {items.length === 0 && (
                                    <div className="font-body text-xs text-ink-soft py-4 text-center">
                                        No low-confidence rows. Clean!
                                    </div>
                                )}
                                {items.map((it) => (
                                    <label
                                        key={it.id}
                                        className="flex items-center gap-2 px-2 py-1.5 hover:bg-ink/5 border-b border-ink/10"
                                        data-testid={`cleanup-row-${it.id}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selected.has(it.id)}
                                            onChange={() => toggleSelect(it.id)}
                                            data-testid={`cleanup-row-select-${it.id}`}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-body text-xs text-ink truncate">
                                                {it.summary || it.id.slice(0, 8)}
                                            </div>
                                            <div className="font-body text-[10px] text-ink-soft">
                                                {it.domain} · {it.source_type || "?"} ·{" "}
                                                conf <span className="font-mono">{(it.confidence || 0).toFixed(2)}</span>
                                                {it.structured?.date_source && ` · date: ${it.structured.date_source}`}
                                            </div>
                                        </div>
                                        <div className="font-body text-[10px] text-ink-soft whitespace-nowrap">
                                            {(it.timestamp || it.ingested_at || "").slice(0, 10)}
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </>
                    )}

                    {!loading && tab === "tags" && (
                        <div
                            className="flex flex-wrap gap-2"
                            data-testid="cleanup-tags-cloud"
                        >
                            {tags.map((t) => (
                                <span
                                    key={t.tag}
                                    className="font-body text-xs px-2 py-1 border border-ink/30 bg-ink/5 hover:bg-ink/10"
                                    title={`${t.count} occurrences · avg confidence ${t.avg_confidence}`}
                                    data-testid={`cleanup-tag-${t.tag}`}
                                >
                                    {t.tag}
                                    <sup className="ml-1 text-ink-soft text-[9px]">{t.count}</sup>
                                </span>
                            ))}
                            {tags.length === 0 && (
                                <div className="font-body text-xs text-ink-soft">No tags meet the threshold.</div>
                            )}
                        </div>
                    )}

                    {!loading && tab === "breakdown" && summary && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs font-body">
                            <div>
                                <div className="uppercase tracking-[0.18em] text-ink-soft text-[10px] mb-1">By tier</div>
                                {(summary.tiers || []).map((t, i) => (
                                    <div key={i} className="flex justify-between border-b border-ink/10 py-1">
                                        <span>≥ {t.min_confidence}</span>
                                        <span className="font-mono">{t.count}</span>
                                    </div>
                                ))}
                            </div>
                            <div>
                                <div className="uppercase tracking-[0.18em] text-ink-soft text-[10px] mb-1">By domain</div>
                                {(summary.by_domain || []).map((d, i) => (
                                    <div key={i} className="flex justify-between border-b border-ink/10 py-1">
                                        <span>{d.domain}</span>
                                        <span className="font-mono">{d.count}</span>
                                    </div>
                                ))}
                            </div>
                            <div>
                                <div className="uppercase tracking-[0.18em] text-ink-soft text-[10px] mb-1">By date_source</div>
                                {(summary.by_date_source || []).map((d, i) => (
                                    <div key={i} className="flex justify-between border-b border-ink/10 py-1">
                                        <span>{d.date_source}</span>
                                        <span className="font-mono">{d.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
